// Path: frontend/src/components/QuickBooksSync/FieldMappingDesigner.jsx
// Inputs: mode ('pipeline' | 'standard'), inline (bool), token, config, standardMapping, sourceSystem, onSave, onClose, onResetDefaults, onSaved, onPipelineUpdated
// Outputs: Reusable unified field mapping designer, inspect/diff analyzer, and live sync execution component
// Summary: Core unified pipeline interface supporting field mapping design with searchable ERP source table dropdown populating all canonical Passport.Tables when source is Passport merged with live database tables, dynamic source table switching upon QB target changes, live sample data preview, rich column selection, inspect & diff comparisons, direct sync execution, and clean standard mode isolation.

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import MappingDesignerTable from './shared/MappingDesignerTable';
import DiffAnalyzerView from './shared/DiffAnalyzerView';
import SyncRunnerView from './shared/SyncRunnerView';
import SearchableSelect from './SearchableSelect';
import { QUICKBOOKS_TARGET_ENTITIES, getQbEntityMeta, normalizeEntityName } from './entityCatalogs';


// Auto-injected unbox helper for standardized API responses
const unbox = async (res) => {
    const payload = await res.json();
    if (payload && typeof payload === 'object' && payload.success === true && payload.data !== undefined) {
        return payload.data;
    }
    return payload;
};

// Helper to merge target fields (official + discovered custom) with existing mapping assignments
const mergeTargetFieldsWithMappings = (tFields, cFields, rawMappings) => {
    const allTargets = [...(tFields || [])];
    (cFields || []).forEach(cf => {
        if (!allTargets.some(t => t.name.toLowerCase() === cf.name.toLowerCase())) {
            allTargets.push(cf);
        }
    });

    const existingMap = new Map();
    (rawMappings || []).forEach(m => {
        if (m && m.target) {
            existingMap.set(m.target.toLowerCase(), {
                target: m.target,
                sources: Array.isArray(m.sources) && m.sources.length > 0
                    ? m.sources
                    : (m.source ? (m.source.includes('+') ? m.source.split('+').map(s => s.trim()) : [m.source]) : []),
                source: m.source || (Array.isArray(m.sources) ? m.sources.join(m.delimiter !== undefined ? m.delimiter : ' - ') : ''),
                delimiter: m.delimiter !== undefined ? m.delimiter : ' - ',
                template: m.template || ''
            });
        }
    });

    const result = [];
    // 1. All known target fields in canonical order
    for (const tf of allTargets) {
        const lower = tf.name.toLowerCase();
        if (existingMap.has(lower)) {
            result.push(existingMap.get(lower));
            existingMap.delete(lower);
        } else {
            result.push({
                target: tf.name,
                sources: [],
                source: '',
                delimiter: ' - ',
                template: ''
            });
        }
    }

    // 2. Any custom mappings previously added not present in target fields list
    for (const remaining of existingMap.values()) {
        result.push(remaining);
    }

    return result;
};

// Helper to extract nested live sample values from QuickBooks sample records
const getLiveQbSampleValue = (fieldPath, sample) => {
    if (!sample) return '(No sample)';
    if (!fieldPath) return '';
    const parts = fieldPath.split('.');
    let val = sample;
    for (const p of parts) {
        if (val === null || val === undefined) return '(Empty in sample)';
        val = val[p];
    }
    if (val === null || val === undefined || val === '') return '(Empty in sample)';
    if (typeof val === 'object') {
        return JSON.stringify(val);
    }
    return String(val);
};

export const FieldMappingDesigner = ({
    mode = 'pipeline', // 'pipeline' or 'standard'
    inline = false,
    token,
    config = {},
    standardMapping = {},
    sourceSystem: explicitSourceSystem,
    onSave,
    onSaved,
    onClose,
    onResetDefaults,
    onPipelineUpdated
}) => {
    const isStandardMode = mode === 'standard';

    // Effective source system determination
    const effectiveSourceSystem = (
        explicitSourceSystem ||
        (isStandardMode ? standardMapping.SourceSystem : config.SourceSystem) ||
        'Passport'
    ).trim();
    const isPassportSource = effectiveSourceSystem.toLowerCase() === 'passport';

    // Sub-view Tab for Pipeline Mode: 'mappings' | 'diff' | 'sync'
    const [pipelineTab, setPipelineTab] = useState('mappings');

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState(null);
    const [definitions, setDefinitions] = useState(null);
    const [mappings, setMappings] = useState([]);
    const [searchFilter, setSearchFilter] = useState('');
    const [isDirty, setIsDirty] = useState(false);

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isDirty) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty]);

    const handleClose = async () => {
        if (isDirty && !(await useSyncStore.getState().showConfirm('You have unsaved changes. Are you sure you want to close?'))) {
            return;
        }
        if (onClose) onClose();
    };

    // Active Entity and Target QuickBooks Entity
    const initialEntity = isStandardMode
        ? normalizeEntityName(standardMapping.EntityType || standardMapping.TargetQBEntity || 'Customer')
        : normalizeEntityName(config.TargetQBEntity || config.EntityType || 'Customer');
    const [selectedEntity, setSelectedEntity] = useState(initialEntity);

    const initialQBEntity = isStandardMode
        ? normalizeEntityName(standardMapping.TargetQBEntity || standardMapping.EntityType || 'Customer')
        : normalizeEntityName(config.TargetQBEntity || config.EntityType || 'Customer');
    const [selectedQBEntity, setSelectedQBEntity] = useState(initialQBEntity);

    // Source Table State
    const meta = getQbEntityMeta(selectedQBEntity);
    const initialTable = isStandardMode
        ? (standardMapping.SourceTable || meta.defaultTable || '')
        : (config.TableName || meta.defaultTable || '');
    const [selectedTable, setSelectedTable] = useState(initialTable);

    // Automatically switch source table to associated table when QB target entity or incoming configuration changes
    useEffect(() => {
        const targetEntity = isStandardMode
            ? normalizeEntityName(standardMapping.TargetQBEntity || standardMapping.EntityType || 'Customer')
            : normalizeEntityName(config.TargetQBEntity || config.EntityType || 'Customer');

        setSelectedQBEntity(targetEntity);
        setSelectedEntity(targetEntity);

        const targetMeta = getQbEntityMeta(targetEntity);
        const associatedTable = isStandardMode
            ? (standardMapping.SourceTable || targetMeta.defaultTable || '')
            : (config.TableName || targetMeta.defaultTable || '');

        if (associatedTable) {
            setSelectedTable(associatedTable);
        }
    }, [
        isStandardMode,
        config.ConfigID,
        config.TargetQBEntity,
        config.EntityType,
        config.TableName,
        standardMapping.MappingID,
        standardMapping.TargetQBEntity,
        standardMapping.EntityType,
        standardMapping.SourceTable,
        standardMapping.UpdatedAt
    ]);

    const [availableTables, setAvailableTables] = useState([]);
    const [loadingTables, setLoadingTables] = useState(false);

    // QuickBooks Custom Fields
    const [qbCustomFields, setQbCustomFields] = useState([]);
    const [loadingCustomFields, setLoadingCustomFields] = useState(false);

    // Live Samples
    const [sampleRecords, setSampleRecords] = useState([]);
    const [activeSampleIndex, setActiveSampleIndex] = useState(0);
    const [loadingSamples, setLoadingSamples] = useState(false);

    // Live QuickBooks Samples
    const [qbSampleRecords, setQbSampleRecords] = useState([]);
    const [activeQbSampleIndex, setActiveQbSampleIndex] = useState(0);
    const [loadingQbSamples, setLoadingQbSamples] = useState(false);

    // Add Custom Mapping State
    const [newTarget, setNewTarget] = useState('');
    const [newSource, setNewSource] = useState('');

    // Pipeline Active Toggle State
    const [isActive, setIsActive] = useState(config.IsActive !== false);

    // Inspect & Diff State
    const [diffLimit, setDiffLimit] = useState(50);
    const [loadingDiff, setLoadingDiff] = useState(false);
    const [diffPreviewData, setDiffPreviewData] = useState(null);
    const [diffError, setDiffError] = useState(null);
    const [diffDetailTab, setDiffDetailTab] = useState('changes'); // 'changes' | 'qb_sample' | 'pbs_sample'

    // Sync Execution State
    const [syncLimit, setSyncLimit] = useState(50);
    const [loadingSync, setLoadingSync] = useState(false);
    const [syncResult, setSyncResult] = useState(null);
    const [showSyncConfirm, setShowSyncConfirm] = useState(false);

    // Fetch live tables from the source database profile or Passport table dictionary
    useEffect(() => {
        let isMounted = true;
        const fetchAvailableTables = async () => {
            setLoadingTables(true);
            const connPrefix = config.SourceConnPrefix || standardMapping.SourceConnPrefix;
            const dbName = config.SourceDatabaseName || standardMapping.SourceDatabaseName || 'pbsdata00';

            try {
                let passportTables = [];
                // 1. If Source is Passport (or Standard Mode), load canonical Passport.Tables from internal database
                if (isPassportSource || isStandardMode) {
                    try {
                        const dictRes = await fetch('/api/sync/data-dictionary/tables', {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (dictRes.ok) {
                            const data = await unbox(dictRes);
                            if (Array.isArray(data)) {
                                passportTables = data.map(t => ({
                                    name: t.TableName,
                                    description: t.Description || (t.Module ? `${t.Module} Table` : 'Passport Table'),
                                    module: t.Module,
                                    fieldCount: t.FieldCount,
                                    isCanonical: true
                                }));
                            }
                        }
                    } catch (e) {
                        console.warn('[FieldMappingDesigner] Error fetching Passport.Tables:', e);
                    }
                }

                // 2. If a live database connection is configured, also fetch live database tables
                let liveDbTables = [];
                if (connPrefix && dbName) {
                    try {
                        const params = new URLSearchParams();
                        params.append('connPrefix', connPrefix);
                        params.append('databaseName', dbName);

                        const res = await fetch(`/api/sync/database-tables?${params.toString()}`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (res.ok) {
                            const data = await unbox(res);
                            const rawList = Array.isArray(data) ? data : (Array.isArray(data?.tables) ? data.tables : []);
                            liveDbTables = rawList.map(t => {
                                const tName = typeof t === 'string' ? t : (t.name || t.TABLE_NAME || '');
                                return {
                                    name: tName,
                                    description: (typeof t === 'object' && t.description) ? t.description : undefined,
                                    module: (typeof t === 'object' && t.module) ? t.module : undefined,
                                    isLive: true
                                };
                            }).filter(t => t.name);
                        }
                    } catch (err) {
                        console.warn('[FieldMappingDesigner] Could not fetch live database tables:', err);
                    }
                }

                if (!isMounted) return;

                if (isPassportSource || passportTables.length > 0) {
                    const passportMap = new Map();
                    passportTables.forEach(t => passportMap.set(t.name.toUpperCase(), t));

                    const combined = [];
                    const seenNames = new Set();

                    // Prioritize live database tables matching connected database
                    liveDbTables.forEach(lt => {
                        const upper = lt.name.toUpperCase();
                        if (seenNames.has(upper)) return;
                        seenNames.add(upper);

                        // Match base table (strip company suffix digits e.g. CUSFIL00 -> CUSFIL)
                        const baseName = upper.replace(/\d+$/, '');
                        const matched = passportMap.get(baseName) || passportMap.get(upper);

                        combined.push({
                            name: lt.name,
                            description: matched ? (matched.description || matched.name) : 'Live Database Table',
                            module: matched ? matched.module : undefined,
                            isLive: true
                        });
                    });

                    // Append all canonical Passport.Tables to guarantee all 221 tables remain selectable
                    passportTables.forEach(pt => {
                        const upper = pt.name.toUpperCase();
                        if (!seenNames.has(upper)) {
                            seenNames.add(upper);
                            combined.push(pt);
                        }
                    });

                    setAvailableTables(combined);
                } else {
                    setAvailableTables(liveDbTables);
                }
            } catch (err) {
                console.error('[FieldMappingDesigner] Error assembling available tables:', err);
            } finally {
                if (isMounted) setLoadingTables(false);
            }
        };

        fetchAvailableTables();
        return () => { isMounted = false; };
    }, [token, config.SourceConnPrefix, config.SourceDatabaseName, standardMapping.SourceConnPrefix, standardMapping.SourceDatabaseName, isStandardMode, isPassportSource]);

    // Active table name fallback
    const activeTableName = useMemo(() => {
        if (selectedTable && selectedTable.trim()) return selectedTable.trim();
        if (config.TableName && config.TableName.trim()) return config.TableName.trim();
        if (standardMapping.SourceTable && standardMapping.SourceTable.trim()) return standardMapping.SourceTable.trim();
        return meta.defaultTable || 'CUSFIL';
    }, [selectedTable, config.TableName, standardMapping.SourceTable, meta.defaultTable]);

    // Format available database/schema tables for SearchableSelect
    const tableSelectOptions = useMemo(() => {
        const list = (availableTables || []).map(t => ({
            value: t.name,
            label: t.name,
            description: t.description || (t.module ? `Module: ${t.module}` : ''),
            sublabel: t.isLive ? '[Live DB]' : (t.module ? `[${t.module}]` : '[Passport]')
        }));

        const current = (selectedTable || activeTableName || '').trim().toUpperCase();
        if (current && !list.some(o => o.value.toUpperCase() === current)) {
            list.unshift({
                value: current,
                label: current,
                description: 'Active ERP Table',
                sublabel: '[Current]'
            });
        }
        return list;
    }, [availableTables, selectedTable, activeTableName]);

    // Extract primitive values for stable dependency tracking
    const sourceConnPrefix = config.SourceConnPrefix || standardMapping.SourceConnPrefix || '';
    const sourceDatabaseName = config.SourceDatabaseName || standardMapping.SourceDatabaseName || 'pbsdata00';
    const rawMappingsStr = isStandardMode
        ? (standardMapping.FieldMappingsJson || (Array.isArray(standardMapping.mappings) ? JSON.stringify(standardMapping.mappings) : ''))
        : (config.FieldMappingsJson || config.FieldMappings || '');

    // Fetch Field Definitions (ERP columns & QB target fields)
    const fetchDefinitions = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                entityType: selectedEntity,
                sourceConnPrefix,
                sourceDatabaseName,
                tableName: activeTableName
            });

            const res = await fetch(`/api/sync/field-definitions/${encodeURIComponent(selectedEntity)}?${params.toString()}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!res.ok) {
                const errData = await unbox(res).catch(() => ({}));
                throw new Error(errData.error || `Failed to load field definitions (HTTP ${res.status})`);
            }
            const data = await unbox(res);
            setDefinitions(data);

            // Raw initial mappings
            let initialRaw = [];
            if (rawMappingsStr) {
                try {
                    const parsed = typeof rawMappingsStr === 'string' ? JSON.parse(rawMappingsStr) : rawMappingsStr;
                    if (Array.isArray(parsed)) initialRaw = parsed;
                } catch (e) {}
            }

            const targetFields = data.targetFields || [];
            const merged = mergeTargetFieldsWithMappings(targetFields, qbCustomFields, initialRaw);
            setMappings(merged); setIsDirty(false);
        } catch (err) {
            console.error('[FieldMappingDesigner] Error fetching definitions:', err);
            setSaveMessage({ type: 'error', text: err.message });
        } finally {
            setLoading(false);
        }
    }, [selectedEntity, sourceConnPrefix, sourceDatabaseName, activeTableName, token, isStandardMode, rawMappingsStr, qbCustomFields]);

    useEffect(() => {
        fetchDefinitions();
    }, [fetchDefinitions]);

    // Fetch Live Samples from PBS ERP source
    const fetchSamples = useCallback(async () => {
        if (isStandardMode || !activeTableName) return;
        setLoadingSamples(true);
        try {
            const params = new URLSearchParams({
                sourceConnPrefix: sourceConnPrefix || '',
                sourceDatabaseName: sourceDatabaseName || 'pbsdata00',
                tableName: activeTableName,
                entityType: selectedEntity,
                limit: '10'
            });

            const res = await fetch(`/api/sync/pbs-preview?${params.toString()}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (res.ok) {
                const data = await unbox(res);
                const recs = Array.isArray(data.sampleRecords) && data.sampleRecords.length > 0
                    ? data.sampleRecords
                    : (Array.isArray(data.records) && data.records.length > 0 ? data.records : []);
                if (recs.length > 0) {
                    setSampleRecords(recs);
                    setActiveSampleIndex(0);
                } else {
                    setSampleRecords([]);
                }
            }
        } catch (err) {
            console.warn('[FieldMappingDesigner] Could not fetch PBS samples:', err);
        } finally {
            setLoadingSamples(false);
        }
    }, [isStandardMode, activeTableName, sourceConnPrefix, sourceDatabaseName, selectedEntity, token]);

    // Fetch Live Destination Samples from QuickBooks Online
    const fetchQbSamples = useCallback(async () => {
        if (isStandardMode) return;
        const accountId = config.AccountID || standardMapping.AccountID;
        if (!accountId) return;

        setLoadingQbSamples(true);
        try {
            const params = new URLSearchParams({
                accountId: String(accountId),
                entity: selectedQBEntity,
                limit: '10'
            });

            const res = await fetch(`/api/sync/qb-preview?${params.toString()}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (res.ok) {
                const data = await unbox(res);
                const recs = Array.isArray(data.sampleRecords) && data.sampleRecords.length > 0
                    ? data.sampleRecords
                    : (Array.isArray(data.records) && data.records.length > 0 ? data.records : []);
                if (recs.length > 0) {
                    setQbSampleRecords(recs);
                    setActiveQbSampleIndex(0);
                } else {
                    setQbSampleRecords([]);
                }
            }
        } catch (err) {
            console.warn('[FieldMappingDesigner] Could not fetch QB samples:', err);
        } finally {
            setLoadingQbSamples(false);
        }
    }, [isStandardMode, config.AccountID, standardMapping.AccountID, selectedQBEntity, token]);

    useEffect(() => {
        if (!isStandardMode) {
            fetchSamples();
            fetchQbSamples();
        }
    }, [isStandardMode, fetchSamples, fetchQbSamples]);

    // Active Samples
    const activeSample = sampleRecords[activeSampleIndex] || null;
    const activeQbSample = qbSampleRecords[activeQbSampleIndex] || null;

    // Source Columns Options
    const sourceColumns = useMemo(() => {
        return definitions?.sourceFields || [];
    }, [definitions]);

    const sourceSelectOptions = useMemo(() => {
        const sampleLookup = {};
        if (activeSample && typeof activeSample === 'object') {
            for (const key of Object.keys(activeSample)) {
                sampleLookup[key.toLowerCase().trim()] = activeSample[key];
            }
        }

        return sourceColumns.map(col => {
            const colKey = col.name.toLowerCase().trim();
            const rawVal = sampleLookup[colKey];
            let sampleSnippet = '';
            if (rawVal !== undefined && rawVal !== null && String(rawVal).trim() !== '') {
                const s = String(rawVal).trim();
                sampleSnippet = s.length > 25 ? `${s.slice(0, 25)}...` : s;
            }

            const typePk = `${col.type || 'string'}${col.isPrimaryKey ? ' • PK' : ''}`;
            const subText = sampleSnippet ? `[Sample: "${sampleSnippet}"] • ${typePk}` : typePk;

            return {
                value: col.name,
                label: `${col.name}${col.description ? ` (${col.description})` : ''}`,
                subText,
                sampleValue: sampleSnippet || null
            };
        });
    }, [sourceColumns, activeSample]);

    // Target Fields Options
    const targetSelectOptions = useMemo(() => {
        const official = (definitions?.targetFields || []).map(f => ({
            value: f.name,
            label: `${f.name}${f.description ? ` (${f.description})` : ''}`,
            subText: f.type || 'String'
        }));
        const custom = qbCustomFields.map(f => ({
            value: f.name,
            label: `${f.name} (Custom Field)`,
            subText: f.type || 'Custom String'
        }));
        return [...official, ...custom];
    }, [definitions, qbCustomFields]);

    // Filtered Mappings List
    const filteredMappings = useMemo(() => {
        if (!searchFilter.trim()) return mappings;
        const q = searchFilter.toLowerCase();
        return mappings.filter(m =>
            (m.target && m.target.toLowerCase().includes(q)) ||
            (m.source && m.source.toLowerCase().includes(q)) ||
            (Array.isArray(m.sources) && m.sources.some(s => s.toLowerCase().includes(q)))
        );
    }, [mappings, searchFilter]);

    // Multi-source builder actions
    const handleAddSourceColumn = (targetName, colName) => {
        if (!colName) return;
        setIsDirty(true); setMappings(prev => prev.map(m => {
            if (m.target.toLowerCase() === targetName.toLowerCase()) {
                const curSources = Array.isArray(m.sources) ? m.sources : (m.source ? [m.source] : []);
                if (curSources.includes(colName)) return m;
                const nextSources = [...curSources, colName];
                const delim = m.delimiter !== undefined ? m.delimiter : ' - ';
                return {
                    ...m,
                    sources: nextSources,
                    source: nextSources.join(delim)
                };
            }
            return m;
        }));
    };

    const handleRemoveSourceColumn = (targetName, colIndex) => {
        setIsDirty(true); setMappings(prev => prev.map(m => {
            if (m.target.toLowerCase() === targetName.toLowerCase()) {
                const curSources = Array.isArray(m.sources) ? m.sources : (m.source ? [m.source] : []);
                const nextSources = curSources.filter((_, idx) => idx !== colIndex);
                const delim = m.delimiter !== undefined ? m.delimiter : ' - ';
                return {
                    ...m,
                    sources: nextSources,
                    source: nextSources.join(delim)
                };
            }
            return m;
        }));
    };

    const handleUpdateDelimiter = (targetName, newDelimiter) => {
        setIsDirty(true); setMappings(prev => prev.map(m => {
            if (m.target.toLowerCase() === targetName.toLowerCase()) {
                const curSources = Array.isArray(m.sources) ? m.sources : (m.source ? [m.source] : []);
                return {
                    ...m,
                    delimiter: newDelimiter,
                    source: curSources.join(newDelimiter)
                };
            }
            return m;
        }));
    };

    const handleUnassignSource = (targetName) => {
        setIsDirty(true); setMappings(prev => prev.map(m => {
            if (m.target.toLowerCase() === targetName.toLowerCase()) {
                return {
                    ...m,
                    sources: [],
                    source: '',
                    template: ''
                };
            }
            return m;
        }));
    };

    const handleAddCustomMapping = () => {
        if (!newTarget.trim() || !newSource.trim()) return;
        const exists = mappings.some(m => m.target.toLowerCase() === newTarget.trim().toLowerCase());
        if (exists) {
            useSyncStore.getState().showAlert(`Target field "${newTarget.trim()}" is already present in the mappings list.`);
            return;
        }

        const newEntry = {
            target: newTarget.trim(),
            sources: [newSource.trim()],
            source: newSource.trim(),
            delimiter: ' - ',
            template: ''
        };

        setMappings(prev => [newEntry, ...prev]);
        setIsDirty(true);
        setNewTarget('');
        setNewSource('');
    };

    const handleCustomMappingChange = (targetName, field, val) => {
        setIsDirty(true);
        setMappings(prev => prev.map(m => {
            if (m.target.toLowerCase() === targetName.toLowerCase()) {
                const updated = { ...m, [field]: val };
                if (field === 'sources') {
                    const delim = m.delimiter !== undefined ? m.delimiter : ' - ';
                    updated.source = Array.isArray(val) ? val.join(delim) : String(val);
                }
                return updated;
            }
            return m;
        }));
    };

    const handleRemoveCustomRow = (targetName) => {
        setMappings(prev => prev.filter(m => m.target.toLowerCase() !== targetName.toLowerCase()));
        setIsDirty(true);
    };

    // Load standard preset into current editor
    const handleLoadStandardPreset = async () => {
        if (!(await useSyncStore.getState().showConfirm(`Overwrite current mappings with standard presets for ${selectedEntity}?`))) return;
        setLoading(true);
        try {
            const res = await fetch(`/api/sync/standard-mappings?sourceSystem=Passport`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await unbox(res);
                const std = (data.mappings || []).find(m =>
                    (m.TargetQBEntity || m.EntityType || '').toLowerCase() === selectedQBEntity.toLowerCase()
                );
                if (std && std.mappings) {
                    const targetFields = definitions?.targetFields || [];
                    const merged = mergeTargetFieldsWithMappings(targetFields, qbCustomFields, std.mappings);
                    setMappings(merged); setIsDirty(true);
                    setSaveMessage({ type: 'success', text: 'Standard preset loaded into designer!' });
                } else {
                    useSyncStore.getState().showAlert(`No standard mapping preset found for entity "${selectedQBEntity}".`);
                }
            }
        } catch (err) {
            useSyncStore.getState().showAlert(`Failed to load standard preset: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Toggle active state for pipeline
    const handleToggleActive = async (checked) => {
        setIsActive(checked);
        if (!config.ConfigID) return;
        try {
            await fetch('/api/sync/configs/toggle-active', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ configId: config.ConfigID, isActive: checked })
            });
            if (onPipelineUpdated) onPipelineUpdated();
        } catch (e) {
            console.error('Failed to toggle active state:', e);
        }
    };

    // Save field mappings
    const handleSave = async (e) => {
        if (e) e.preventDefault();
        setSaving(true);
        setSaveMessage(null);
        try {
            const cleanMappings = mappings
                .filter(m => m.target && ((Array.isArray(m.sources) && m.sources.length > 0) || (m.source && m.source.trim().length > 0) || (m.template && m.template.trim().length > 0)))
                .map(m => ({
                    target: m.target.trim(),
                    source: m.source ? m.source.trim() : (m.sources ? m.sources.join(m.delimiter || ' - ') : ''),
                    sources: Array.isArray(m.sources) && m.sources.length > 0 ? m.sources : [(m.source || '').trim()],
                    delimiter: m.delimiter !== undefined ? m.delimiter : ' - ',
                    template: m.template || undefined
                }));

            if (isStandardMode) {
                if (onSave) {
                    await onSave({
                        sourceTable: activeTableName,
                        targetQBEntity: selectedQBEntity,
                        mappings: cleanMappings
                    });
                }
                setSaveMessage({ type: 'success', text: 'Standard mapping preset saved successfully!' });
                setIsDirty(false);
            } else {
                const payload = {
                    configId: config.ConfigID,
                    tableName: activeTableName,
                    targetQBEntity: selectedQBEntity,
                    fieldMappingsJson: JSON.stringify(cleanMappings)
                };
                const res = await fetch('/api/sync/configs/field-mappings', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(payload)
                });
                if (!res.ok) {
                    const err = await unbox(res);
                    throw new Error(err.error || 'Failed to save field mappings');
                }
                setSaveMessage({ type: 'success', text: `Field mappings for ${config.ConfigName || activeTableName} saved!` });
                setIsDirty(false);
                if (onSaved) onSaved();
                if (onPipelineUpdated) onPipelineUpdated();
            }
        } catch (err) {
            setSaveMessage({ type: 'error', text: err.message });
        } finally {
            setSaving(false);
        }
    };

    // Run Inspect & Diff Analysis
    const handleRunDiff = async () => {
        setLoadingDiff(true);
        setDiffError(null);
        try {
            const payload = {
                configId: config.ConfigID,
                accountId: config.AccountID,
                sourceConnPrefix: config.SourceConnPrefix,
                sourceDatabaseName: config.SourceDatabaseName,
                tableName: activeTableName,
                targetQBEntity: selectedQBEntity,
                entityType: config.EntityType || selectedEntity,
                limit: diffLimit
            };

            const res = await fetch('/api/sync/sync/preview', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const err = await unbox(res);
                throw new Error(err.error || 'Dry run diff analysis failed');
            }

            const data = await unbox(res);
            setDiffPreviewData(data);
        } catch (err) {
            console.error('Diff error:', err);
            setDiffError(err.message);
        } finally {
            setLoadingDiff(false);
        }
    };

    // Execute Live Batch Sync
    const handleExecuteLiveSync = async (isDryRun = false) => {
        setLoadingSync(true);
        setSyncResult(null);
        setShowSyncConfirm(false);
        try {
            const endpoint = isDryRun ? '/api/sync/sync/preview' : '/api/sync/sync/execute';
            const payload = {
                configId: config.ConfigID,
                accountId: config.AccountID,
                sourceConnPrefix: config.SourceConnPrefix,
                sourceDatabaseName: config.SourceDatabaseName,
                tableName: activeTableName,
                targetQBEntity: selectedQBEntity,
                entityType: config.EntityType || selectedEntity,
                limit: syncLimit
            };

            const res = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const err = await unbox(res);
                throw new Error(err.error || 'Sync execution failed');
            }

            const data = await unbox(res);
            setSyncResult(data);
            if (onSaved) onSaved();
            if (onPipelineUpdated) onPipelineUpdated();
        } catch (err) {
            useSyncStore.getState().showAlert(`Execution Error: ${err.message}`);
        } finally {
            setLoadingSync(false);
        }
    };

    const activeMappedCount = mappings.filter(m => (m.sources && m.sources.length > 0) || (m.source && m.source.trim())).length;

    return (
        <div className={`flex flex-col bg-slate-900 text-slate-200 ${inline ? 'w-full rounded-xl border border-slate-750 shadow-xl overflow-hidden' : 'h-full overflow-hidden'}`}>
            
            {/* Header & Tabs */}
            <div className="flex flex-col border-b border-slate-750 bg-slate-800 shrink-0">
                <div className="flex justify-between items-center px-4 py-3">
                    <div>
                        <h2 className="text-lg font-semibold text-white">
                            {isStandardMode ? 'Standard Mapping Designer' : 'Field Mapping Designer'}
                        </h2>
                        <div className="text-sm text-slate-400 mt-1">
                            {isStandardMode 
                                ? `Preset: ${standardMapping?.MappingName || 'Unknown'} (${effectiveSourceSystem} → QuickBooks)` 
                                : `Pipeline: ${config?.ConfigName || 'Unknown'} (${effectiveSourceSystem} → QuickBooks)`}
                        </div>
                    </div>
                    <div className="flex space-x-3 items-center">
                        <span className="text-xs text-slate-400">
                            {activeMappedCount} columns mapped
                        </span>
                        {isStandardMode && onResetDefaults && (
                            <button onClick={onResetDefaults} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 text-sm font-medium rounded transition">
                                Reset Defaults
                            </button>
                        )}
                        <button 
                            onClick={handleSave} 
                            disabled={saving}
                            className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded shadow transition disabled:opacity-50"
                        >
                            {saving ? 'Saving...' : 'Save Mappings'}
                        </button>
                        {onClose && (
                            <button onClick={handleClose} className="p-2 text-slate-400 hover:text-white transition">
                                ✕
                            </button>
                        )}
                    </div>
                </div>

                {/* Tab Navigation (Pipeline Mode Only) */}
                {!isStandardMode && (
                    <div className="flex space-x-6 px-4 bg-slate-800/50 pt-2">
                        <button
                            onClick={() => setPipelineTab('mappings')}
                            className={`pb-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                                pipelineTab === 'mappings'
                                    ? 'border-blue-500 text-blue-400'
                                    : 'border-transparent text-slate-400 hover:text-slate-300'
                            }`}
                        >
                            Mappings
                        </button>
                        <button
                            onClick={() => setPipelineTab('diff')}
                            className={`pb-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                                pipelineTab === 'diff'
                                    ? 'border-blue-500 text-blue-400'
                                    : 'border-transparent text-slate-400 hover:text-slate-300'
                            }`}
                        >
                            Inspect & Diff
                        </button>
                        <button
                            onClick={() => setPipelineTab('sync')}
                            className={`pb-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                                pipelineTab === 'sync'
                                    ? 'border-emerald-500 text-emerald-400'
                                    : 'border-transparent text-slate-400 hover:text-slate-300'
                            }`}
                        >
                            Sync Execution
                        </button>
                    </div>
                )}
            </div>

            <div className="flex-1 overflow-hidden relative flex flex-col">
                {/* Source Table Selector Bar (Visible for both Standard Mode and Pipeline Mode) */}
                {pipelineTab === 'mappings' && (
                    <div className="px-4 py-2.5 bg-slate-850 border-b border-slate-750 flex flex-wrap items-center justify-between gap-3 text-xs shrink-0">
                        <div className="flex items-center gap-2.5">
                            <span className="text-slate-300 font-semibold flex items-center gap-1.5">
                                <span className="material-symbols-outlined text-base text-sky-400">table_chart</span>
                                ERP Source Table:
                            </span>
                            <SearchableSelect
                                options={tableSelectOptions}
                                value={selectedTable || activeTableName}
                                onChange={val => {
                                    const nextTable = (val || '').toUpperCase();
                                    setSelectedTable(nextTable);
                                    setIsDirty(true);
                                }}
                                placeholder="Search or enter ERP source table..."
                                allowCustom={true}
                                className="w-72"
                            />
                            <span className="text-slate-600">&bull;</span>
                            <span className="text-slate-400">
                                Total Available Columns: <strong className="text-slate-200">{sourceColumns.length}</strong>
                            </span>
                            {loading && (
                                <span className="text-indigo-400 flex items-center gap-1 text-[11px] ml-2">
                                    <span className="material-symbols-outlined text-sm animate-spin">sync</span>
                                    Loading definitions...
                                </span>
                            )}
                        </div>

                        <div className="flex items-center gap-3">
                            <span className="text-slate-400">
                                Target Entity: <strong className="text-purple-300 font-mono">{selectedQBEntity}</strong>
                            </span>
                            <span className="text-slate-600">&bull;</span>
                            <span className="text-slate-400">
                                Active Mappings: <strong className="text-emerald-400 font-mono">{activeMappedCount}</strong>
                            </span>
                        </div>
                    </div>
                )}

                {/* TAB 1: MAPPINGS */}
                {pipelineTab === 'mappings' && (
                    <MappingDesignerTable
                        sourceColumns={sourceColumns}
                        isStandardMode={isStandardMode}
                        searchFilter={searchFilter}
                        setSearchFilter={setSearchFilter}
                        selectedQBEntity={selectedQBEntity}
                        qbSampleRecords={qbSampleRecords}
                        activeQbSampleIndex={activeQbSampleIndex}
                        setActiveQbSampleIndex={setActiveQbSampleIndex}
                        pbsSampleRecords={sampleRecords}
                        activeSampleIndex={activeSampleIndex}
                        setActiveSampleIndex={setActiveSampleIndex}
                        activeTableName={activeTableName}
                        filteredMappings={filteredMappings}
                        definitions={definitions}
                        newTarget={newTarget}
                        setNewTarget={setNewTarget}
                        newSource={newSource}
                        setNewSource={setNewSource}
                        handleAddCustomMapping={handleAddCustomMapping}
                        handleCustomMappingChange={handleCustomMappingChange}
                        handleRemoveCustomRow={handleRemoveCustomRow}
                    />
                )}

                {/* TAB 2: DIFF */}
                {pipelineTab === 'diff' && (
                    <DiffAnalyzerView
                        loadingPreview={loadingDiff}
                        diffPreviewData={diffPreviewData}
                        diffDetailTab={diffDetailTab}
                        setDiffDetailTab={setDiffDetailTab}
                        selectedQBEntity={selectedQBEntity}
                        handleGenerateDiff={handleRunDiff}
                    />
                )}

                {/* TAB 3: SYNC EXECUTION RUNNER */}
                {pipelineTab === 'sync' && (
                    <SyncRunnerView
                        selectedQBEntity={selectedQBEntity}
                        syncLimit={syncLimit}
                        setSyncLimit={setSyncLimit}
                        loadingSync={loadingSync}
                        syncResult={syncResult}
                        handleExecuteLiveSync={handleExecuteLiveSync}
                    />
                )}
            </div>
        </div>
    );
};

export default FieldMappingDesigner;
